/**
 * ShellColumns - the session's frame: the actor and the target as narrow rails either side of the centre (the log
 * with the map in it), each the one click to its full panel. The only layout since the classic three columns were
 * retired (v12.0.0, plan retire-classic). The contents come in as nodes; this owns only the frame.
 */
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import styles from '../../styles/DeathWatchSimulator.module.css';
import railStyles from '../../styles/SideRails.module.css';
import { ActorRail, TargetRail } from './SideRails';

/**
 * THE RAILS (plan map-display, E; the session study): the actor and the target
 * as narrow rails either side of the centre, each the one click to its full
 * panel, which opens over the map's edge with the focus in it (Escape or ✕
 * closes it and gives the focus back to its rail). The two open and close on
 * their own, both at once if wanted (the user, 2026-10-20; they used to swap).
 * The action drum lives in the actor's panel, so opening the drum opens it.
 * The panels stay mounted while closed - their refs are read once.
 */
const NONE_OPEN = { actor: false, target: false };

/**
 * Open `side` whenever `on` turns true - the drum its actor panel, the handouts drawer its target panel. During the
 * render, not in an effect: the panel is shown in the SAME commit as what opened it, so the drum focusing itself as
 * it opens finds its panel visible. A browser refuses the focus inside a hidden panel, and F with the panel shut left
 * the arrow keys turning nothing (2026-10-22). `seen` starts false, so a screen loading with `on` opens the panel too.
 */
function useOpensWith(on, side, setOpen) {
  const [seen, setSeen] = useState(false);
  if (on !== seen) {
    setSeen(on);
    if (on) setOpen((was) => ({ ...was, [side]: true }));
  }
}

/**
 * Which panels are open, and the verbs. The handouts drawer is drawn in the
 * target panel (the right column's top), so the two move together: Handouts
 * opens the panel, and the panel closing - its rail, Escape, a click on the map
 * - closes Handouts, or its button would read pressed over nothing (the user,
 * 2026-10-20: "pressing the Handouts button doesn't do anything").
 */
function useRailsOpen({ drumOpen, handoutsOpen, onCloseHandouts, storyFirst }) {
  const [open, setOpen] = useState(NONE_OPEN);
  useOpensWith(drumOpen, 'actor', setOpen);
  useOpensWith(handoutsOpen, 'target', setOpen);
  // STORY FIRST (2026-10-22, plan retire-classic): entering Narrative opens the target panel, Story in front (the
  // phase's own tab); closed, it stays shut until Narrative is entered again - only the change opens it.
  useOpensWith(storyFirst, 'target', setOpen);
  // On the panel's CLOSING only: a screen that loads with Handouts open has the panel shut for one render.
  const targetWas = useRef(open.target);
  useEffect(() => {
    if (targetWas.current && !open.target && handoutsOpen) onCloseHandouts?.();
    targetWas.current = open.target;
  }, [open.target, handoutsOpen, onCloseHandouts]);
  const toggle = (side) => () => setOpen((was) => ({ ...was, [side]: !was[side] }));
  const close = (side) => () => setOpen((was) => ({ ...was, [side]: false }));
  // The narrow contract anything drawn inside the rails gets: open one side, or close both (null: a click on the map).
  const openRail = useCallback((side) => setOpen((was) => (side ? { ...was, [side]: true } : NONE_OPEN)), []);
  return { open, toggle, close, openRail };
}

/**
 * @param {object} props
 * @param {React.ReactNode} props.left - the actor's panel
 * @param {React.ReactNode} props.centre - the log, holding the map
 * @param {React.ReactNode} props.right - the target's panel (with the handouts, the story and the stealth fronts)
 * @param {object[]} [props.railRows] - the order's rows, for the actor rail's muster
 * @param {object} [props.targetFront] - what the target rail names (frontWaiting)
 * @param {boolean} [props.drumOpen] - the action drum is open: its panel opens
 * @param {boolean} [props.handoutsOpen] - the handouts drawer is open: the target panel (its home) opens
 * @param {Function} [props.onCloseHandouts] - close the drawer: called when the target panel closes under it
 * @param {boolean} [props.storyFirst] - the screen is in Narrative: entering it opens the target panel
 */
export default function ShellColumns({ left, centre, right, railRows, targetFront, ...opening }) {
  const { open, toggle, close, openRail } = useRailsOpen(opening);
  return (
    <RailsOpenContext.Provider value={openRail}>
      <div className={styles.mainLayout} data-rails="true">
        <ActorRail open={open.actor} onToggle={toggle('actor')} railRows={railRows} />
        <div
          className={styles.panelCenter}
          // Which panels stand over the map's edges: what lies over the map keeps clear of them (the stylesheets).
          data-open-actor={open.actor || undefined}
          data-open-target={open.target || undefined}
        >
          {/* The log's region holds the log alone (2026-10-22): the panels stand beside it, so reading the log never
              reads a closed panel's text. */}
          <div className={styles.centreLog} aria-label="Combat log">
            {centre}
          </div>
          <RailPanel side="actor" label="Attacker panel" open={open.actor} onClose={close('actor')}>
            {left}
          </RailPanel>
          <RailPanel side="target" label="Target panel" open={open.target} onClose={close('target')}>
            {right}
          </RailPanel>
        </div>
        <TargetRail open={open.target} onToggle={toggle('target')} front={targetFront} />
      </div>
    </RailsOpenContext.Provider>
  );
}

/**
 * `openRail(side)`, for anything drawn inside the rails (the stealth strip
 * over the map opens the target panel on its Stealth tab, plan design-gaps
 * L5), leaving the other as it is; `openRail(null)` closes both (a click on
 * bare map, Z6c). Null outside a session (the Assets editors).
 */
export const RailsOpenContext = createContext(null);

/**
 * Whether the rail panel something is drawn in is open - true outside the rails. The actor's status curtain forgets,
 * when its panel closes, that the cursor had worked in it (2026-10-23).
 */
export const RailPanelOpenContext = createContext(true);

/**
 * A full panel opened from its rail, over the map's edge; hidden, still mounted,
 * when closed. Escape is handled HERE, on the panel, and goes no further: the
 * window's Escape also clears a focused reaction, which closing a panel must not.
 */
function RailPanel({ side, label, open, onClose, children }) {
  const ref = useRef(null);
  // Whatever held the focus when the panel opened (its rail, mostly) gets it back on close. What opened the panel
  // from INSIDE it keeps the focus it took: the drum focuses its cylinder in its own effect, which runs first.
  const opener = useRef(null);
  useEffect(() => {
    if (!open || ref.current?.contains(document.activeElement)) return;
    opener.current = document.activeElement;
    ref.current?.focus();
  }, [open]);
  const close = () => {
    onClose();
    opener.current?.focus?.();
  };
  // The panel's Escape is its own: stopped here, it never reaches the window's (which also clears a reaction). An
  // Escape pressed inside a dialog drawn in the panel (a picker, an editor) is that dialog's, never the panel's: the
  // condition picker's Escape closed the panel round it too (2026-10-23).
  const onKeyDown = (e) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    if (e.target.closest('[role="dialog"]') !== e.currentTarget) return;
    e.stopPropagation();
    close();
  };
  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- a dialog's own Escape
    <section
      ref={ref}
      // A panel over the map that takes the focus: a non-modal dialog, its Escape its own.
      role="dialog"
      aria-modal="false"
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className={railStyles.panel}
      data-side={side}
      hidden={!open}
      aria-label={label}
      data-testid={`rail-panel-${side}`}
    >
      <button
        type="button"
        className={railStyles.close}
        aria-label={`Close the ${label.toLowerCase()}`}
        onClick={close}
      >
        ✕
      </button>
      <RailPanelOpenContext.Provider value={open}>{children}</RailPanelOpenContext.Provider>
    </section>
  );
}
